import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IMasterSuiteTouch, IMasterSuiteTouchAsset } from 'src/app/models/mastersuites/mastersuite';
import {
  AssetMappingAction,
  IAssetMapping,
} from 'src/app/models/products/actions/settings/transferProductSettings';

@Component({
  selector: 'app-asset-mapping-sub',
  templateUrl: './asset-mapping-sub.component.html',
  styleUrls: ['./asset-mapping-sub.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class AssetMappingSubComponent implements OnInit {
  @Input() sourceTouch!: IMasterSuiteTouch;
  @Input() destinationTouch!: IMasterSuiteTouch;
  @Input() sourceEnhancementFlaggedAssetIds: string[] = [];
  @Input() destinationEnhancementFlaggedAssetIds: string[] = [];
  @Output() assetMapChanged = new EventEmitter<IAssetMapping[]>();

  poolSources: IMasterSuiteTouchAsset[] = [];
  sourcesBySlot: { [destinationAssetId: string]: IMasterSuiteTouchAsset[] } = {};
  expandedAssetIds = new Set<string>();

  ngOnInit(): void {
    const partition = this.partitionSourcesIntoSlotsAndPool();
    this.sourcesBySlot = partition.slots;
    this.poolSources = partition.pool;
    this.emitAssetMap();
  }

  get sourceAssets(): IMasterSuiteTouchAsset[] {
    return [...(this.sourceTouch?.masterSuiteTouchAssets ?? [])].sort((a, b) => a.packageAssetIndex - b.packageAssetIndex);
  }

  get destinationAssets(): IMasterSuiteTouchAsset[] {
    return [...(this.destinationTouch?.masterSuiteTouchAssets ?? [])].sort((a, b) => a.packageAssetIndex - b.packageAssetIndex);
  }

  isSourceEnhancementFlagged(asset: IMasterSuiteTouchAsset): boolean {
    return (this.sourceEnhancementFlaggedAssetIds ?? []).includes(asset.id!);
  }

  isDestinationEnhancementFlagged(asset: IMasterSuiteTouchAsset): boolean {
    return (this.destinationEnhancementFlaggedAssetIds ?? []).includes(asset.id!);
  }

  assetLabel(asset: IMasterSuiteTouchAsset): string {
    return `${asset.packageAssetType} · ${asset.packageAssetRobotOutDegrees}° · table ${asset.packageAssetTablePosition}`;
  }

  slotFor(destinationAssetId: string): IMasterSuiteTouchAsset[] {
    return this.sourcesBySlot[destinationAssetId] ?? [];
  }

  isExpanded(asset: IMasterSuiteTouchAsset): boolean {
    return this.expandedAssetIds.has(asset.id!);
  }

  toggleExpanded(asset: IMasterSuiteTouchAsset): void {
    const next = new Set(this.expandedAssetIds);
    if (next.has(asset.id!)) next.delete(asset.id!);
    else next.add(asset.id!);
    this.expandedAssetIds = next;
  }

  onDrop(event: CdkDragDrop<IMasterSuiteTouchAsset[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
    this.emitAssetMap();
  }

  canEnterSlot = (drag: CdkDrag, drop: CdkDropList): boolean => {
    if (this.isSameContainer(drag, drop)) return true;
    if (this.isSlotOccupied(drop)) return false;
    return this.dragMatchesDestination(drag, drop);
  };

  private isSameContainer(drag: CdkDrag, drop: CdkDropList): boolean {
    return drag.dropContainer === drop;
  }

  private isSlotOccupied(drop: CdkDropList): boolean {
    return (drop.data as IMasterSuiteTouchAsset[]).length > 0;
  }

  private dragMatchesDestination(drag: CdkDrag, drop: CdkDropList): boolean {
    const destination = this.destinationForSlot(drop);
    if (!destination) return false;
    return this.assetsMatch(drag.data as IMasterSuiteTouchAsset, destination);
  }

  private destinationForSlot(drop: CdkDropList): IMasterSuiteTouchAsset | undefined {
    const destinationAssetId = Object.entries(this.sourcesBySlot).find(([, sources]) => sources === drop.data)?.[0];
    return this.destinationAssets.find((asset) => asset.id === destinationAssetId);
  }

  private assetsMatch(source: IMasterSuiteTouchAsset, destination: IMasterSuiteTouchAsset): boolean {
    return source.packageAssetType === destination.packageAssetType
      && source.packageAssetRobotOutDegrees === destination.packageAssetRobotOutDegrees
      && source.packageAssetTablePosition === destination.packageAssetTablePosition;
  }

  private emitAssetMap(): void {
    const mappedFromSlots: IAssetMapping[] = this.destinationAssets.flatMap((destinationAsset) =>
      this.slotFor(destinationAsset.id!).map((source) => ({
        sourceAssetId: source.id!,
        action: AssetMappingAction.Map,
        destinationAssetId: destinationAsset.id!,
      })),
    );
    const discarded: IAssetMapping[] = this.poolSources.map((source) => ({
      sourceAssetId: source.id!,
      action: AssetMappingAction.Discard,
    }));
    this.assetMapChanged.emit([...mappedFromSlots, ...discarded]);
  }

  private partitionSourcesIntoSlotsAndPool(): {
    slots: { [destinationAssetId: string]: IMasterSuiteTouchAsset[] };
    pool: IMasterSuiteTouchAsset[];
  } {
    const emptySlots = Object.fromEntries(
      this.destinationAssets.map((destinationAsset) => [destinationAsset.id!, [] as IMasterSuiteTouchAsset[]]),
    );
    return this.sourceAssets.reduce<{
      slots: { [destinationAssetId: string]: IMasterSuiteTouchAsset[] };
      pool: IMasterSuiteTouchAsset[];
    }>((accumulator, source) => {
      const suggested = this.findSuggestedDestinationAsset(source);
      if (!suggested || (accumulator.slots[suggested.id!] ?? []).length > 0) {
        return { ...accumulator, pool: [...accumulator.pool, source] };
      }
      return {
        ...accumulator,
        slots: { ...accumulator.slots, [suggested.id!]: [source] },
      };
    }, { slots: emptySlots, pool: [] });
  }

  private findSuggestedDestinationAsset(sourceAsset: IMasterSuiteTouchAsset): IMasterSuiteTouchAsset | undefined {
    return this.destinationAssets.find((destinationAsset) =>
      destinationAsset.packageAssetType === sourceAsset.packageAssetType &&
      destinationAsset.packageAssetRobotOutDegrees === sourceAsset.packageAssetRobotOutDegrees &&
      destinationAsset.packageAssetTablePosition === sourceAsset.packageAssetTablePosition,
    );
  }
}
